import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { OnboardingFlowType } from '../config/onboardingConfig';
import { getStepPathsForFlow } from '../config/onboardingConfig';

interface OnboardingContextType {
  /** Current step index when modal is open; null when closed */
  currentStep: number | null;
  setCurrentStep: (step: number | null) => void;
  /** Which flow is active (user or admin). Used for sidebar highlight and step config. */
  flowType: OnboardingFlowType;
  setFlowType: (flow: OnboardingFlowType) => void;
  /** Check if href matches the current step's paths for the active flow */
  isOnboardingStepHighlight: (href: string) => boolean;
  /** True when current step has no paths (e.g. admin step 0 = mode switch at bottom of sidebar) */
  isOnboardingModeSwitchStep: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [flowType, setFlowType] = useState<OnboardingFlowType>('user');

  const isOnboardingStepHighlight = (href: string): boolean => {
    if (currentStep == null) return false;
    const pathsPerStep = getStepPathsForFlow(flowType);
    const paths = pathsPerStep[currentStep];
    if (!paths || paths.length === 0) return false;
    return paths.includes(href);
  };

  const pathsForCurrentStep = currentStep != null ? getStepPathsForFlow(flowType)[currentStep] : null;
  const isOnboardingModeSwitchStep =
    currentStep != null && Array.isArray(pathsForCurrentStep) && pathsForCurrentStep.length === 0;

  return (
    <OnboardingContext.Provider
      value={{ currentStep, setCurrentStep, flowType, setFlowType, isOnboardingStepHighlight, isOnboardingModeSwitchStep }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
